// PurchaseMixin.ts

import { Component, Mixins, Prop } from 'vue-property-decorator';
import { AthleteProfileModel, BAEventModel, BAPurchaseModel } from '@/models'
import { baPurchaseApi } from '@/api/BAPurchaseApi';
import { baEventApi } from '@/api/BAEventsApi';
import { athleteApi } from '@/api/AthleteApi';
import { AthletesCacheMixin } from './AthletesCacheMixin';

@Component
export class PurchaseMixin extends Mixins(AthletesCacheMixin) {
    @Prop() purchaseRequest: BAPurchaseModel;

    showPurchaseDialog: boolean = false;
    waitForStripe: boolean = false;
    waitForPurchase: boolean = false;

    AthleteLabel(label: string, athleteKeys: Array<string>): string {
        const athleteLabel = this.NormalizeString(label);
        let athleteKey = athleteKeys.find(k => k.toLowerCase() === athleteLabel);
        if( this.IsEmpty(athleteKey) ) {
            // some of the AthleteProfileModel keys do not map directly to expected labels
            if( athleteLabel === "dob" ) athleteKey = "birthDate"; 
            else if( athleteLabel.includes("birth") ) athleteKey = "birthDate";
            else if( athleteLabel === "gpa" ) athleteKey = "highschoolGPA";
            else if( athleteLabel === "sat" ) athleteKey = "SATScore";
            else if( athleteLabel === "act" ) athleteKey = "ACTScore";
            else if( athleteLabel === "gradyear" ) athleteKey = "highschoolGradYear";
        } else {
            // some of the AthleteProfileModel keys map to the wrong labels
            if( athleteLabel === "gradyear" ) athleteKey = "highschoolGradYear";
            else if( athleteLabel.includes("phone") ) athleteKey = "phone";
        }
        if( this.IsNotEmpty(athleteKey) ) return athleteKey;
        return label;
    }
    DataLabel(label: string, dataKeys: Array<string>): string {
        const dataLabel = this.NormalizeString(label);
        let dataKey = dataKeys.find(k => this.NormStringIncludes(dataLabel, k));
        if( this.IsEmpty(dataKey) ) {
            if( dataLabel === "phone" ) dataKey = "Phone Number";
        }
        if( this.IsNotEmpty(dataKey) ) return dataKey;
        return label;
    }
    PurchaseAthleteData(labels: Array<string>, athleteProfile: AthleteProfileModel): Record<string,any> {
        let athleteLabels: Record<string, any> = {};
        const athleteKeys = this.IsNotEmpty(athleteProfile)? Object.keys(athleteProfile) : [];
        const dataKeys = this.IsNotEmpty(athleteProfile?.data)? Object.keys(athleteProfile?.data) : [];
        for( const label of labels ) {
            const athleteLabel = this.AthleteLabel(label, athleteKeys);
            const dataLabel = this.DataLabel(label, dataKeys);
            if( athleteLabel === "birthDate" ) athleteLabels[label] = athleteKeys.includes(athleteLabel)? this.formatDatePretty(athleteProfile[athleteLabel], false) : "";
            if( this.IsEmpty(athleteLabels[label]) ) athleteLabels[label] = this.getDataCaseInsensitive(athleteProfile?.data, label);
            if( this.IsEmpty(athleteLabels[label]) ) athleteLabels[label] = this.getDataCaseInsensitive(athleteProfile?.data, athleteLabel);
            if( this.IsEmpty(athleteLabels[label]) ) athleteLabels[label] = this.getDataCaseInsensitive(athleteProfile, athleteLabel);
            if( this.IsEmpty(athleteLabels[label]) ) athleteLabels[label] = this.getDataCaseInsensitive(athleteProfile?.data, dataLabel);
        }
        return athleteLabels;
    }

    /**
     * Retrieves the value associated with a 'searchKey' from a 'data' object,
     * ignoring the capitalization of the keys in the 'data' object.
     *
     * @param data The object to search within (Record<string, any>).
     * @param searchKey The key whose value to retrieve (string).
     * @returns The value associated with the key if found, or undefined if no match is found.
     */
    getDataCaseInsensitive(data: Record<string, any>, searchKey: string): any | undefined {
        // 1. Basic validation
        if (!data || typeof data !== 'object') {
            return undefined;
        }

        // 2. Normalize the key we are looking for to lowercase
        const lowerSearchKey = searchKey.toLowerCase();

        // 3. Iterate over the actual keys in the data object
        for (const dataKey in data) {
            // Ensure we are only checking the object's own properties
            if (Object.prototype.hasOwnProperty.call(data, dataKey)) {
                
                // 4. Compare the object's key (normalized to lowercase) with the search key
                if (dataKey.toLowerCase() === lowerSearchKey) {
                    // 5. If they match, return the value using the original key from the data object
                    if( this.IsNotEmpty(data[dataKey])) return data[dataKey];
                }
            }
        }

        // 6. If the loop completes, no match was found
        return undefined;
    }    

    async completePurchaseRequest(purchase: BAPurchaseModel, athleteProfile: AthleteProfileModel, event: BAEventModel = undefined): Promise<BAPurchaseModel> {
        this.waitForPurchase = true;
        const completedPurchase: BAPurchaseModel = await baEventApi.addParticipant(purchase.productId, purchase.priceId, purchase.parentId);

        // update athlete data
        if( this.IsNotEmpty(purchase.data) ) {
            const athleteKeys = Object.keys(athleteProfile);
            const labels = Object.keys(purchase.data);
            athleteProfile.data = athleteProfile.data?? {};
            labels.forEach(label => {
                const athleteLabel = this.AthleteLabel(label, athleteKeys);
                athleteProfile.data[athleteLabel] = purchase.data[label];
            })
        }

        let nextUrl = athleteProfile.nextUrl;
        if( this.IsNotEmpty(event) && nextUrl?.includes(event.id) ) nextUrl = "";
        await athleteApi.patch({id: purchase.parentId, nextUrl, data: athleteProfile.data});
        this.waitForPurchase = false;
        return completedPurchase;
    }
    async savePurchaseRequest(): Promise<BAPurchaseModel> {
        this.waitForStripe = true;
        try {
            return await baPurchaseApi.save(this.purchaseRequest);
        } catch(e) {
            throw new Error(`Unable to save purchase request: parent=${this.purchaseRequest?.parentId}, product=${this.purchaseRequest?.productId}`);
        }
    }

    async deletePurchase(parentId, eventId, ticketId) {
        const purchase: BAPurchaseModel = await this.PurchaseRetrieve(undefined, parentId, eventId);
        if( this.IsEmpty(purchase) ) return;
        
        await baPurchaseApi.delete(purchase);
    }

    onShowPurchaseDialog() {
        this.showPurchaseDialog = true;
    }
    onHidePurchaseDialog() {
        this.showPurchaseDialog = false;
    }
}